import { useState } from 'react'
import { DataTable } from '@/components/data-table'
import { getAttendeeColumns, type DisplayAttendee } from './attendee-columns'
import type { SortingState } from '@tanstack/react-table'
import AttendeePaymentDrawer from './AttendeePaymentDrawer'
import useDebounce from '@/hooks/use-debounce'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ATTENDEE_QUERY_KEYS } from '@/api/queryKeys'
import {
  fetchAttendees,
  getAttendeeBookingExport,
  getAttendeeExport,
  sendTicket,
  type AttendeesParams,
} from '@/api/attendeeApi'
import {
  handleAxiosError,
  safeDownloadFile,
  transformAttendeeData,
} from '@/utils/helper'
import type { Attendee } from '@/schemas/attendee.schema'
import type { FilterValues } from '../FilterDrawer'
import { showErrorToast, showSuccessToast } from '../ui/toast'

interface AttendeeDataProps {
  readonly searchQuery?: string
}

function AttendeeData({ searchQuery = '' }: AttendeeDataProps) {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [sorting, setSorting] = useState<SortingState>([])
  const [pagination, setPagination] = useState({
    pageIndex: currentPage - 1,
    pageSize: pageSize,
  })

  // Attendee payment drawer state
  const [attendeeDrawerOpen, setAttendeeDrawerOpen] = useState(false)
  const [selectedAttendeeId, setSelectedAttendeeId] = useState<string | null>(
    null
  )
  const debouncedSearchTerm = useDebounce(searchQuery, 500)
  const [resendingId, setResendingId] = useState<string | null>(null)

  const [filters, setFilters] = useState<FilterValues>({
    userRole: [],
    subscriptionType: '',
    eventAttendance: '',
    registrationEndDate: '',
    status: [],
    category: [],
    registrationStartDate: '',
    statusEvent: '',
    purchaseDate: '',
    eventId: '',
  })

  const {
    data: attendeesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      ATTENDEE_QUERY_KEYS.all,
      currentPage,
      pageSize,
      debouncedSearchTerm,
      sorting,
      filters,
    ],
    queryFn: () =>
      fetchAttendees({
        limit: pageSize,
        page: currentPage,
        search: debouncedSearchTerm || undefined,
        sort: sorting,
        status: Array.isArray(filters.status)
          ? filters.status
          : [filters.status],
        purchaseDate: filters.purchaseDate,
        eventId: filters.eventId || undefined,
      }),
  })

  const { mutate: exportAttendee, isPending: isExportingAttendee } =
    useMutation({
      mutationFn: getAttendeeExport,
      onSuccess: blob =>
        safeDownloadFile({ data: blob, fileName: 'attendee-export.csv' }),
      onError: err => {
        handleAxiosError('Failed to export report. Please try again.', err)
      },
    })

  const {
    mutate: exportAttendeeBooking,
    isPending: isExportingAttendeeBooking,
  } = useMutation({
    mutationFn: getAttendeeBookingExport,
    onSuccess: blob =>
      safeDownloadFile({ data: blob, fileName: 'attendee-booking-export.csv' }),
    onError: err => {
      handleAxiosError('Failed to export report. Please try again.', err)
    },
  })

  const { mutate: sendTicketMutation, isPending: sendingTicket } = useMutation({
    mutationFn: (transactionId: string) => sendTicket(transactionId),
    onSuccess: () => {
      showSuccessToast(`Ticket successfully re-sent to attendee.`)
      setResendingId(null)
    },
    onError: err => {
      handleAxiosError('Failed to resend ticket. Please try again.', err)
      setResendingId(null)
    },
  })

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePageSizeChange = (size: number) => {
    setPageSize(size)
    setCurrentPage(1) // Reset to first page when changing page size
  }

  const handleFiltersChange = (newFilters: FilterValues) => {
    setFilters(newFilters)
    setCurrentPage(1) // Reset to first page when filters change
  }

  const handleAttendeeClick = (attendeeId: string) => {
    setSelectedAttendeeId(attendeeId)
    setAttendeeDrawerOpen(true)
  }

  const handleSendTicketClick = (transactionId: string) => {
    setResendingId(transactionId)
    sendTicketMutation(transactionId)
  }

  const totalItems = attendeesData?.data?.totalCount || 0
  const totalPages =
    attendeesData?.data?.totalPages || Math.ceil(totalItems / pageSize)

  const rawAttendees: Attendee[] = attendeesData?.data?.result || []
  const DisplayAttendee: DisplayAttendee[] = rawAttendees.map(
    transformAttendeeData
  )

  const handleExportClick = () => {
    if (DisplayAttendee.length === 0) {
      showErrorToast(
        'No data available for export',
        'Please adjust filters or try again later.'
      )
      return
    }

    const params: AttendeesParams = {
      search: debouncedSearchTerm,
      status: Array.isArray(filters.status) ? filters.status : [],
      purchaseDate: filters.purchaseDate,
      sort: sorting,
      eventId: filters.eventId,
    }

    exportAttendee(params)
  }

  const handleAttendeeBookingExportClick = () => {
    if (DisplayAttendee.length === 0) {
      showErrorToast(
        'No data available for export',
        'Please adjust filters or try again later.'
      )
      return
    }

    const params: AttendeesParams = {
      search: debouncedSearchTerm,
      status: Array.isArray(filters.status) ? filters.status : [],
      purchaseDate: filters.purchaseDate,
      sort: sorting,
      eventId: filters.eventId,
    }

    exportAttendeeBooking(params)
  }

  return (
    <section aria-label="All Attendees Table">
      <DataTable<DisplayAttendee, unknown>
        parentClassName="mt-0"
        title="All Attendees"
        showEventSelect={true}
        attendeeFilter={true}
        data={DisplayAttendee}
        columns={getAttendeeColumns(
          handleAttendeeClick,
          handleSendTicketClick,
          resendingId
        )}
        sorting={sorting}
        setSorting={setSorting}
        pagination={pagination}
        setPagination={setPagination}
        onExportClick={handleExportClick}
        onSecondExportClick={handleAttendeeBookingExportClick}
        filterButtonText="Filter"
        exportButtonText="Export Attendee CSV"
        secondExportButtonText="Export Attendee Booking CSV"
        showFilter={true}
        showExport={true}
        isExportLoading={isExportingAttendee}
        isSecondExportLoading={isExportingAttendeeBooking}
        showSecondExport={true}
        isLoading={isLoading}
        error={error?.message || null}
        totalItems={totalItems}
        currentPage={currentPage}
        pageSize={pageSize}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onFiltersChange={handleFiltersChange}
        emptyState={{
          image: '/auth/empty-userlist.svg',
          title: 'No attendees found',
          subtitle:
            'There are no attendees available at the moment. Please check back later or adjust your filters.',
        }}
      />

      {/* Attendee Payment Drawer */}
      {selectedAttendeeId && (
        <AttendeePaymentDrawer
          open={attendeeDrawerOpen}
          onOpenChange={setAttendeeDrawerOpen}
          onBackToEventDetails={() => {
            setAttendeeDrawerOpen(false)
            setSelectedAttendeeId(null)
          }}
          eventId={selectedAttendeeId}
          onResendTicketClick={handleSendTicketClick}
          isResending={sendingTicket}
        />
      )}
    </section>
  )
}

export default AttendeeData
